/* ============================================================================
   Mock data — learning content, marketplace, campaigns, community and the
   assistant's scripted knowledge base.
   ========================================================================== */

import type {
  Campaign,
  ChatMessage,
  CommunityChannel,
  Lesson,
  Nursery,
  Product,
  Quiz,
} from '@core/types'
import { isoFromNow, isoMonthsAgo } from './climate'

export const LESSONS: Lesson[] = [
  {
    id: 'ls-01',
    title: 'Reading your soil before you plant',
    summary:
      'Three checks that take ten minutes and decide whether a seedling survives its first summer: texture, depth and salt crust.',
    category: 'soil',
    readingMinutes: 4,
    facts: [
      'Squeeze a handful of moist soil: if it holds a ribbon longer than 5 cm it is clay-heavy and drains slowly.',
      'A white crust after irrigation means salts are accumulating at the surface — leach with a heavy watering and add organic matter.',
      'Dig a 40 cm hole before buying anything. Hardpan at 20 cm is the most common cause of failed street planting in Iraq.',
    ],
  },
  {
    id: 'ls-02',
    title: 'Watering in 45 °C heat without wasting water',
    summary:
      'When to water, how much, and why evening watering saves up to a third of every litre.',
    category: 'water',
    readingMinutes: 5,
    facts: [
      'Water before 8 a.m. or after sunset. Midday watering loses 30–40% to evaporation.',
      'Deep, infrequent watering builds deeper roots; light daily sprinkling trains shallow, fragile roots.',
      'A 10 cm mulch layer cuts evaporation from the soil surface by roughly a third.',
    ],
  },
  {
    id: 'ls-03',
    title: 'Species that survive Iraqi salinity',
    summary:
      'Why Sidr, Athel and date palm keep growing where lemon trees fail, and how to use that in a street planting plan.',
    category: 'species',
    readingMinutes: 6,
    facts: [
      'Athel tamarisk tolerates irrigation water several times saltier than most crops accept.',
      'Date palms tolerate salinity well but still need a leaching programme or yields fall after year five.',
      'Mixing tolerant and sensitive species in one row hides the failure of the sensitive ones until it is too late to replan.',
    ],
  },
  {
    id: 'ls-04',
    title: 'Planting windows and heat stress',
    summary:
      'October to March is the planting window in most of Iraq. Planting in July is the most common avoidable mistake.',
    category: 'climate',
    readingMinutes: 4,
    facts: [
      'A tree planted in November needs roughly half the irrigation of the same tree planted in May.',
      'Windbreaks reduce water loss on the leeward side by lowering wind speed and soil temperature.',
      'Young palms need shade for their first summer — a palm-frond frame is enough.',
    ],
  },
  {
    id: 'ls-05',
    title: 'What to do with pruning waste',
    summary:
      'Burning prunings loses the carbon. Composting returns it to the soil and cuts your fertiliser bill.',
    category: 'recycling',
    readingMinutes: 3,
    facts: [
      'Chipped prunings compost in 8–12 weeks if turned every two weeks and kept moist.',
      'Diseased material (powdery mildew, leaf spot) must not go in a home compost pile — burn or bin it.',
      'One cubic metre of compost covers roughly 20 m² at a 5 cm depth.',
    ],
  },
]

export const QUIZZES: Quiz[] = [
  {
    id: 'qz-01',
    title: 'Salinity tolerance check',
    lessonId: 'ls-03',
    rewardPoints: 40,
    questions: [
      {
        id: 'q1',
        prompt: 'Which species tolerates the most saline irrigation water?',
        options: [
          { id: 'a', label: 'Lemon' },
          { id: 'b', label: 'Athel tamarisk' },
          { id: 'c', label: 'Bougainvillea' },
          { id: 'd', label: 'White mulberry' },
        ],
        correctOptionId: 'b',
        explanation:
          'Athel tamarisk is the most salt-tolerant tree commonly available in Iraqi nurseries. Lemon is the most sensitive of the four.',
      },
      {
        id: 'q2',
        prompt: 'A white salt crust appears around your drip line. What is the first step?',
        options: [
          { id: 'a', label: 'Add more fertiliser' },
          { id: 'b', label: 'Stop watering completely' },
          { id: 'c', label: 'Leach with a heavy watering and add organic matter' },
          { id: 'd', label: 'Replace the plant immediately' },
        ],
        correctOptionId: 'c',
        explanation:
          'Salts accumulate at the surface as water evaporates. A heavy leaching irrigation moves them below the root zone, and organic matter improves structure.',
      },
      {
        id: 'q3',
        prompt: 'Which planting month gives a new tree the best chance in Baghdad?',
        options: [
          { id: 'a', label: 'July' },
          { id: 'b', label: 'September' },
          { id: 'c', label: 'November' },
          { id: 'd', label: 'April' },
        ],
        correctOptionId: 'c',
        explanation:
          'November planting lets roots establish through the cool months. A July planting needs about twice the irrigation and often fails.',
      },
      {
        id: 'q4',
        prompt: 'How far should mulch be kept from the trunk of a young tree?',
        options: [
          { id: 'a', label: 'Touching the trunk, to keep moisture in' },
          { id: 'b', label: 'About 10 cm clear of the trunk' },
          { id: 'c', label: '50 cm clear of the trunk' },
          { id: 'd', label: 'Mulch position does not matter' },
        ],
        correctOptionId: 'b',
        explanation:
          'Mulch against the trunk traps moisture on the bark and invites rot and pests. Keep a 10 cm gap.',
      },
      {
        id: 'q5',
        prompt: 'Why is mixing salt-tolerant and sensitive species in one row risky?',
        options: [
          { id: 'a', label: 'They compete for the same nutrients' },
          {
            id: 'b',
            label: 'The tolerant trees hide the salinity problem until the sensitive ones are dead',
          },
          { id: 'c', label: 'It is not risky, it is recommended' },
          { id: 'd', label: 'It changes the soil pH' },
        ],
        correctOptionId: 'b',
        explanation:
          'The tolerant trees keep the row looking healthy while the sensitive ones decline quietly — you lose a season before noticing.',
      },
    ],
  },
  {
    id: 'qz-02',
    title: 'Watering efficiently in extreme heat',
    lessonId: 'ls-02',
    rewardPoints: 30,
    questions: [
      {
        id: 'q1',
        prompt: 'What time of day wastes the least water?',
        options: [
          { id: 'a', label: 'Midday' },
          { id: 'b', label: 'Early morning or after sunset' },
          { id: 'c', label: 'It makes no measurable difference' },
          { id: 'd', label: 'Late afternoon in full sun' },
        ],
        correctOptionId: 'b',
        explanation: 'Evaporation losses are lowest when the air is cool and the sun is low.',
      },
      {
        id: 'q2',
        prompt: 'Which watering habit builds deeper roots?',
        options: [
          { id: 'a', label: 'A little water every day' },
          { id: 'b', label: 'Deep watering less often' },
          { id: 'c', label: 'Misting the leaves each morning' },
          { id: 'd', label: 'Watering only once the leaves wilt' },
        ],
        correctOptionId: 'b',
        explanation:
          'Deep soaking pushes roots downward where soil stays moist longer. Daily sprinkling keeps roots shallow and fragile.',
      },
    ],
  },
]

/* ------------------------------------------------------------- marketplace -- */

export const NURSERIES: Nursery[] = [
  {
    id: 'nr-01',
    name: 'Al-Rafidain Nursery',
    governorate: 'Baghdad',
    district: 'Al-Adhamiyah',
    verified: true,
    rating: 4.7,
    reviewCount: 312,
    productCount: 48,
    since: '2011',
  },
  {
    id: 'nr-02',
    name: 'Basra Garden Centre',
    governorate: 'Basra',
    district: 'Al-Jubaila',
    verified: true,
    rating: 4.5,
    reviewCount: 208,
    productCount: 36,
    since: '2015',
  },
  {
    id: 'nr-03',
    name: 'Najaf Growers Cooperative',
    governorate: 'Najaf',
    district: 'Kufa',
    verified: true,
    rating: 4.8,
    reviewCount: 476,
    productCount: 62,
    since: '2008',
  },
  {
    id: 'nr-04',
    name: 'Erbil Green House',
    governorate: 'Erbil',
    district: 'Ainkawa',
    verified: false,
    rating: 4.2,
    reviewCount: 74,
    productCount: 29,
    since: '2019',
  },
  {
    id: 'nr-05',
    name: 'Dijlah Agriculture Supplies',
    governorate: 'Babil',
    district: 'Al-Hillah',
    verified: true,
    rating: 4.6,
    reviewCount: 155,
    productCount: 87,
    since: '2013',
  },
]

export const PRODUCTS: Product[] = [
  {
    id: 'pr-01',
    name: 'Sidr seedling, 60–80 cm',
    nameAr: 'شتلة سدر',
    category: 'seedling',
    description:
      'Local provenance Sidr from Kufa stock. Bare-root, bagged, ready for the November planting window.',
    priceIqd: 3_500,
    unit: 'per seedling',
    stock: 1_240,
    deliveryAvailable: true,
    nursery: { id: 'nr-03', name: 'Najaf Growers Cooperative', governorate: 'Najaf', verified: true },
    sponsored: false,
    imageLabel: 'sidr-seedling',
  },
  {
    id: 'pr-02',
    name: 'Date palm offshoot — Barhi variety',
    nameAr: 'فسيلة نخلة برحي',
    category: 'tree',
    description:
      'Two-year offshoot from a producing Barhi mother palm, certified free of bayoud disease.',
    priceIqd: 45_000,
    unit: 'per offshoot',
    stock: 86,
    deliveryAvailable: true,
    nursery: { id: 'nr-02', name: 'Basra Garden Centre', governorate: 'Basra', verified: true },
    sponsored: true,
    imageLabel: 'barhi-offshoot',
  },
  {
    id: 'pr-03',
    name: 'Athel tamarisk, 1.5 m bare root',
    nameAr: 'أثل',
    category: 'tree',
    description:
      'For shelterbelts and saline sites. Survives irrigation water above 8 dS/m where most species fail.',
    priceIqd: 6_000,
    unit: 'per tree',
    stock: 420,
    deliveryAvailable: true,
    nursery: { id: 'nr-03', name: 'Najaf Growers Cooperative', governorate: 'Najaf', verified: true },
    sponsored: false,
    imageLabel: 'athel-bare-root',
  },
  {
    id: 'pr-04',
    name: 'Lemon sapling, grafted on sour orange',
    nameAr: 'شتلة ليمون مطعمة',
    category: 'seedling',
    description:
      'Grafted stock that fruits in year three. Needs clean, low-salinity water — not suitable for Basra canal water.',
    priceIqd: 12_500,
    unit: 'per sapling',
    stock: 63,
    deliveryAvailable: false,
    nursery: { id: 'nr-01', name: 'Al-Rafidain Nursery', governorate: 'Baghdad', verified: true },
    sponsored: false,
    imageLabel: 'lemon-grafted',
  },
  {
    id: 'pr-05',
    name: 'Bougainvillea, 2 litre pot, four colours',
    nameAr: 'جهنمية',
    category: 'seedling',
    description:
      'Flowering stock for walls and pergolas. Colour is selected at the nursery when you collect the order.',
    priceIqd: 8_000,
    unit: 'per pot',
    stock: 210,
    deliveryAvailable: true,
    nursery: { id: 'nr-01', name: 'Al-Rafidain Nursery', governorate: 'Baghdad', verified: true },
    sponsored: false,
    imageLabel: 'bougainvillea-pot',
  },
  {
    id: 'pr-06',
    name: 'Composted soil, 50 kg bag',
    nameAr: 'تربة مكمورة',
    category: 'soil',
    description:
      'Screened compost from date-palm and canal-cleaning waste. Improves structure on heavy clay and saline soils.',
    priceIqd: 9_000,
    unit: 'per 50 kg bag',
    stock: 640,
    deliveryAvailable: true,
    nursery: { id: 'nr-05', name: 'Dijlah Agriculture Supplies', governorate: 'Babil', verified: true },
    sponsored: false,
    imageLabel: 'compost-bag',
  },
  {
    id: 'pr-07',
    name: 'Drip irrigation kit, 50 m with 100 emitters',
    nameAr: 'منظومة ريّ بالتنقيط',
    category: 'tools',
    description:
      'Pressure-compensating emitters that keep working at the low pressure typical of street networks.',
    priceIqd: 78_000,
    unit: 'per kit',
    stock: 34,
    deliveryAvailable: true,
    nursery: { id: 'nr-05', name: 'Dijlah Agriculture Supplies', governorate: 'Babil', verified: true },
    sponsored: false,
    imageLabel: 'drip-kit',
  },
  {
    id: 'pr-08',
    name: 'Copper fungicide concentrate, 1 litre',
    nameAr: 'مبيد فطري نحاسي',
    category: 'fertiliser',
    description:
      'Approved for date palm leaf spot and fruit-tree canker. Dilute 3 ml per litre of water before spraying.',
    priceIqd: 22_000,
    unit: 'per bottle',
    stock: 96,
    deliveryAvailable: true,
    nursery: { id: 'nr-01', name: 'Al-Rafidain Nursery', governorate: 'Baghdad', verified: true },
    sponsored: true,
    imageLabel: 'copper-fungicide',
  },
  {
    id: 'pr-09',
    name: 'Pruning saw, 350 mm',
    nameAr: 'منشار تقليم',
    category: 'tools',
    description: 'Cuts green palm fronds without tearing the crown. Hardened teeth can be resharpened.',
    priceIqd: 34_500,
    unit: 'each',
    stock: 41,
    deliveryAvailable: true,
    nursery: { id: 'nr-04', name: 'Erbil Green House', governorate: 'Erbil', verified: false },
    sponsored: false,
    imageLabel: 'pruning-saw',
  },
  {
    id: 'pr-10',
    name: 'Citrus fertiliser with chelated iron, 5 kg',
    nameAr: 'سماد حمضيات',
    category: 'fertiliser',
    description:
      'Corrects the iron chlorosis that yellows lemon leaves on chalky Iraqi soils. Two applications per season.',
    priceIqd: 16_000,
    unit: 'per 5 kg',
    stock: 0,
    deliveryAvailable: false,
    nursery: { id: 'nr-04', name: 'Erbil Green House', governorate: 'Erbil', verified: false },
    sponsored: false,
    imageLabel: 'citrus-fertiliser',
  },
]

/* --------------------------------------------------------------- campaigns -- */

export const CAMPAIGNS: Campaign[] = [
  {
    id: 'cp-01',
    name: 'Muthanna eastern shelterbelt',
    organisation: 'Ministry of Agriculture — Muthanna Directorate',
    governorate: 'Muthanna',
    district: 'Al-Samawah',
    startsAt: isoFromNow(-120, 7),
    endsAt: isoFromNow(75, 17),
    targetTrees: 25_000,
    plantedTrees: 18_400,
    verifiedTrees: 15_900,
    survivingTrees: 14_200,
    volunteers: 1_240,
    status: 'active',
    speciesSummary: 'Athel tamarisk 60% · Sidr 30% · Euphrates poplar 10%',
  },
  {
    id: 'cp-02',
    name: 'Karrada school green belt',
    organisation: 'Baghdad Municipality with Al-Mansour schools',
    governorate: 'Baghdad',
    district: 'Karrada',
    startsAt: isoMonthsAgo(7, 1),
    endsAt: isoFromNow(48, 17),
    targetTrees: 1_800,
    plantedTrees: 1_410,
    verifiedTrees: 1_260,
    survivingTrees: 1_180,
    volunteers: 320,
    status: 'active',
    speciesSummary: 'White mulberry 45% · Bougainvillea 30% · Sidr 25%',
  },
  {
    id: 'cp-03',
    name: 'Shatt Al-Arab bank restoration',
    organisation: 'Basra Environment Directorate',
    governorate: 'Basra',
    district: 'Shatt Al-Arab',
    startsAt: isoMonthsAgo(22, 15),
    endsAt: isoMonthsAgo(3, 20),
    targetTrees: 12_000,
    plantedTrees: 12_000,
    verifiedTrees: 11_400,
    survivingTrees: 9_700,
    volunteers: 2_150,
    status: 'completed',
    speciesSummary: 'Athel tamarisk 40% · Date palm 35% · Euphrates poplar 25%',
  },
  {
    id: 'cp-04',
    name: 'Erbil foothills reforestation',
    organisation: 'Kurdistan Region Forestry Office',
    governorate: 'Erbil',
    district: 'Shaqlawa',
    startsAt: isoMonthsAgo(5, 3),
    endsAt: isoFromNow(140, 17),
    targetTrees: 8_500,
    plantedTrees: 3_200,
    verifiedTrees: 2_700,
    survivingTrees: 2_540,
    volunteers: 640,
    status: 'active',
    speciesSummary: 'Oak 50% · White mulberry 30% · Lemon 20%',
  },
  {
    id: 'cp-05',
    name: 'Najaf canal greenway — phase 2',
    organisation: 'Najaf Governorate — Green Najaf Initiative',
    governorate: 'Najaf',
    district: 'Kufa',
    startsAt: isoFromNow(28, 7),
    endsAt: isoFromNow(200, 17),
    targetTrees: 6_000,
    plantedTrees: 0,
    verifiedTrees: 0,
    survivingTrees: 0,
    volunteers: 0,
    status: 'planned',
    speciesSummary: 'Not yet planted — seedling contracts being awarded',
  },
]

/* --------------------------------------------------------------- community -- */

export const COMMUNITY_CHANNELS: CommunityChannel[] = [
  {
    id: 'ch-01',
    name: 'Baghdad growers',
    description: 'Street, courtyard and rooftop planting across Baghdad.',
    participants: 2_847,
    lastMessageAt: isoFromNow(0, 9),
  },
  {
    id: 'ch-02',
    name: 'Salinity & soil clinic',
    description:
      'Ask about soil tests, salt crusts and leaching schedules. Agronomists answer on Thursdays.',
    participants: 1_204,
    lastMessageAt: isoFromNow(-1, 21),
  },
  {
    id: 'ch-03',
    name: 'Karrada school garden volunteers',
    description: 'Coordination for the school garden clean-up and re-mulching mission.',
    participants: 132,
    lastMessageAt: isoFromNow(-2, 19),
  },
  {
    id: 'ch-04',
    name: 'Nursery partners — announcements',
    description: 'Verified nurseries post stock, price and delivery updates.',
    participants: 96,
    lastMessageAt: isoFromNow(-4, 11),
  },
]

export const COMMUNITY_MESSAGES: ChatMessage[] = [
  {
    id: 'cm-01',
    channelId: 'ch-03',
    author: { id: 'us-2001', displayName: 'Omar Al-Samarrai', role: 'coordinator' },
    body: 'Karrada school garden mission is confirmed for Saturday at 8 a.m. We have 26 of 40 places filled. Bags, rakes and mulch are on site — bring gloves and water.',
    sentAt: isoFromNow(-2, 19),
    status: 'sent',
  },
  {
    id: 'cm-02',
    channelId: 'ch-01',
    author: { id: 'us-2002', displayName: 'Zainab Abd Al-Rahman Al-Khafaji', role: 'user' },
    body: 'Count me in. Should I bring the Sidr seedlings from Kufa or do we plant what is already stored at the school?',
    sentAt: isoFromNow(-2, 19),
    status: 'sent',
  },
  {
    id: 'cm-03',
    channelId: 'ch-03',
    author: { id: 'us-2003', displayName: 'Hassan Al-Jubouri', role: 'nursery' },
    body: 'We can supply 200 Sidr seedlings at 3,200 IQD each for the school mission if the order is placed before Thursday. Delivery to Karrada is included.',
    sentAt: isoFromNow(-2, 14),
    status: 'sent',
  },
  {
    id: 'cm-04',
    channelId: 'ch-02',
    author: { id: 'us-2004', displayName: 'Fatima Al-Zubaidi', role: 'user' },
    body: 'هذه السنة كانت درجات الحرارة أعلى من المعتاد في الزبير. متى أفضل وقت لري الجهنمية؟ سمعت أن الري وقت الظهيرة يضر النبات.',
    sentAt: isoFromNow(-1, 21),
    status: 'sent',
  },
  {
    id: 'cm-05',
    channelId: 'ch-02',
    author: { id: 'us-2005', displayName: 'Dr. Layla Al-Rubaie', role: 'coordinator' },
    body: 'Watering before 8 a.m. or after sunset is best in your area. Midday watering loses about a third of the water to evaporation and can scorch leaves that still have droplets on them. Deep water every 10 days rather than a little every day.',
    sentAt: isoFromNow(-1, 20),
    status: 'sent',
  },
  {
    id: 'cm-06',
    channelId: 'ch-02',
    author: { id: 'us-2006', displayName: 'Mohammed Al-Dulaimi', role: 'coordinator' },
    body: 'A note for anyone planting lemon in the south: our monitoring shows most failures are caused by saline irrigation water, not by pests. Test the water source before buying grafted stock — a 15,000 IQD test saves the tree and a season of care.',
    sentAt: isoFromNow(-3, 10),
    status: 'sent',
  },
  {
    id: 'cm-07',
    channelId: 'ch-04',
    author: { id: 'us-2003', displayName: 'Hassan Al-Jubouri', role: 'nursery' },
    body: 'Restock: Athel tamarisk bare root is back at 6,000 IQD, and we have 40 date palm offshoots from the Barhi block. Delivery available to Babil and Wasit.',
    sentAt: isoFromNow(-4, 11),
    status: 'sent',
  },
]

/* --------------------------------------------------------------- assistant --
   The assistant is a care assistant: recommendations, care and environmental
   questions. Disease diagnosis is explicitly NOT covered — that stays with the
   computer-vision model, and the assistant says so rather than guessing.
   ========================================================================== */

export const ASSISTANT_LIMITATION =
  'I explain care, planning and environmental questions. I do not diagnose plants from photos — use Disease detection with a clear leaf photo for that.'

export const ASSISTANT_SUGGESTED_PROMPTS: string[] = [
  'Which tree should I plant in Basra with saline water?',
  'How often should I water a young Sidr in August?',
  'Why are my lemon leaves turning yellow?',
  'When is the planting window in Erbil?',
]

export interface AssistantIntent {
  id: string
  keywords: string[]
  reply: string
}

export const ASSISTANT_KNOWLEDGE: AssistantIntent[] = [
  {
    id: 'ai-saline',
    keywords: ['saline', 'salinity', 'salt', 'basra', 'ملحية', 'الملوحة'],
    reply:
      'On saline sites start with Athel tamarisk or Sidr — both tolerate irrigation water far saltier than lemon or most fruit trees accept. If you want fruit, date palm tolerates salinity but needs a leaching programme: every third irrigation, apply twice the normal volume so salts are pushed below the root zone. Test your water source first; a test costs far less than replacing trees.',
  },
  {
    id: 'ai-water',
    keywords: ['water', 'watering', 'irrigation', 'how often', 'ري', 'الري'],
    reply:
      'For a young Sidr in August, water 40 litres in a slow basin every 10–12 days rather than a little every day. Water before 8 a.m. or after sunset so less is lost to evaporation. After the first year you can stretch to every 2–3 weeks. Ten centimetres of mulch, kept clear of the trunk, cuts the requirement by roughly a third.',
  },
  {
    id: 'ai-lemon-yellow',
    keywords: ['yellow', 'yellowing', 'lemon', 'chlorosis', 'اصفرار'],
    reply:
      'Yellow lemon leaves usually come from one of three causes in Iraq: iron chlorosis on chalky soil, saline irrigation water, or waterlogging from a failed drain. Check the newest leaves first — if the veins stay green while the leaf yellows, it is iron deficiency and a chelated iron feed fixes it. If the whole leaf yellows evenly, test the irrigation water. If the soil is waterlogged, reduce frequency before doing anything else.',
  },
  {
    id: 'ai-planting-window',
    keywords: ['when to plant', 'planting window', 'best time', 'erbil', 'موسم الزراعة'],
    reply:
      'Plant between October and March across most of Iraq. November is the sweet spot: roots establish through the cool months and the tree needs roughly half the irrigation of the same tree planted in May. In Erbil and the northern foothills, late February to March also works well. Avoid June to August entirely — the failure rate is very high.',
  },
  {
    id: 'ai-disease',
    keywords: ['disease', 'sick', 'spots', 'fungus', 'diagnos', 'مرض'],
    reply:
      'For a suspected disease, take a photo in daylight with one affected leaf filling most of the frame, then run Disease detection — it returns a confidence score with treatment steps. If the confidence is low, or the species was not part of the training set, show the same photo to a verified nursery and ask them to confirm before applying any chemical.',
  },
  {
    id: 'ai-reward',
    keywords: ['points', 'reward', 'coupon', 'discount', 'نقاط', 'مكافأة'],
    reply:
      'Points come from planting (150), approved verification photos (30), completed care tasks (15–35), missions (90–250) and quizzes (30–40). Spend them in the rewards catalogue on nursery discounts, vouchers, free delivery or the salinity course. Points are awarded only after the action is verified — a photo that does not show the plant clearly is rejected with a reason, not silently ignored.',
  },
  {
    id: 'ai-community',
    keywords: ['mission', 'volunteer', 'community', 'clean', 'مهمة', 'تطوع'],
    reply:
      'Missions are local, time-boxed activities posted by NGOs, municipalities and community coordinators: cleaning and re-mulching a school garden, monitoring street trees, planting drives, or learning tasks. Join one from the Community tab. If the mission needs proof, upload a photo when you finish and the coordinator approves it. Places are limited, so capacity is shown on each card.',
  },
]

export const ASSISTANT_FALLBACK =
  'I did not find that in the care knowledge base yet. Try asking about watering, salinity, planting windows, diseases, rewards or missions — or post the question in the Salinity & soil clinic channel where an agronomist answers on Thursdays.'
